import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Linking,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, ConfirmationModal } from '../components';
import { AttendeeStatusBadge } from '../components/calendar';
import { apiService } from '../services/api';
import { cancelLocalReminders } from '../services/calendar-local-reminders.service';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography, textStyles } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface EventDetailScreenProps {
  eventId?: string;
}

export const EventDetailScreen: React.FC<EventDetailScreenProps> = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, routeParams, activeTab, setActiveTab } = useAppContext();
  const { currentBusiness, user } = useAppStore();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [respondingStatus, setRespondingStatus] = useState<'accepted' | 'declined' | 'tentative' | null>(null);
  const [showLeftPane] = useState(Platform.OS === 'web' ? true : false);
  const [showRightPane] = useState(Platform.OS === 'web' ? true : false);

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const eventId = routeParams?.eventId || '';

  useEffect(() => {
    if (eventId && currentBusiness?._id) {
      loadEvent();
    }
  }, [eventId, currentBusiness]);

  const loadEvent = async () => {
    if (!eventId || !currentBusiness?._id) return;

    try {
      setLoading(true);
      const response = await apiService.getEventById(eventId, currentBusiness._id);

      if (response.statusCode === 1000 && response.data) {
        setEvent(response.data);
      } else {
        const errorMessage = response.errors?.[0]?.message || t('calendar.eventDetail.loadFailed');
        showNotification('error', 'Error', errorMessage);
        navigateTo('calendars');
      }
    } catch (error: any) {
      console.error('Error loading event:', error);
      showNotification('error', 'Error', error.message || t('calendar.eventDetail.loadFailed'));
      navigateTo('calendars');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (eventId) {
      navigateTo('event-edit', eventId);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!eventId || !currentBusiness?._id) return;

    try {
      setDeleting(true);
      const response = await apiService.deleteEvent(eventId, currentBusiness._id);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', t('calendar.eventDetail.deleteSuccess'));
        cancelLocalReminders(eventId).catch((err) =>
          console.error('[EventDetailScreen] Failed to cancel local reminders:', err)
        );
        if (event?.calendarId) {
          navigateTo('calendar-events', event.calendarId);
        } else {
          navigateTo('calendars');
        }
      } else {
        const errorMessage = response.errors?.[0]?.message || t('calendar.eventDetail.deleteFailed');
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || t('calendar.eventDetail.deleteFailed'));
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleRespond = async (status: 'accepted' | 'declined' | 'tentative') => {
    if (!eventId) return;
    try {
      setRespondingStatus(status);
      const response = await apiService.respondToEvent(eventId, status);
      if (response.statusCode === 1000 && response.data) {
        setEvent((prev: any) => ({ ...prev, attendees: response.data.attendees }));
        showNotification('success', 'Success', t('calendar.eventDetail.rsvpUpdated'));
      } else {
        showNotification('error', 'Error', response.errors?.[0]?.message || t('calendar.eventDetail.rsvpFailed'));
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || t('calendar.eventDetail.rsvpFailed'));
    } finally {
      setRespondingStatus(null);
    }
  };

  const handleJoinMeeting = () => {
    if (event?.onlineMeeting?.joinUrl) {
      Linking.openURL(event.onlineMeeting.joinUrl);
    }
  };

  const formatDate = (date: string | Date) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (date: string | Date) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const screenLayoutProps = {
    header: (
      <Header
        showNotifications={true}
        showMessages={true}
        onNotificationPress={() => navigateTo('notifications')}
        onMessagePress={() => navigateTo('chat')}
        showNavigation={Platform.OS === 'web'}
        activeTab={activeTab}
        onTabPress={handleTabPress}
      />
    ),
    showBottomTabs: Platform.OS !== 'web' as any,
    onTabPress: handleTabPress,
    showLeftPane,
    onLeftPaneClose: () => {},
    leftPaneContent: 'settings' as const,
    onSettingsItemPress: async (itemId: string) => {
      const { setPendingSettingsItem } = await import('../utils/tempStorage');
      await setPendingSettingsItem(itemId);
      navigateTo('settings');
    },
    showRightPane,
    onRightPaneClose: () => {},
    rightPaneContent: null as any,
  };

  if (loading) {
    return (
      <ScreenLayout
        {...screenLayoutProps}
        formHeader={
          <FormHeader
            title={t('calendar.eventDetail.title')}
            onBack={() => {
              if (event?.calendarId) {
                navigateTo('calendar-events', event.calendarId);
              } else {
                navigateTo('calendars');
              }
            }}
          />
        }
      >
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>{t('calendar.eventDetail.loading')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  if (!event) {
    return (
      <ScreenLayout
        {...screenLayoutProps}
        formHeader={
          <FormHeader
            title={t('calendar.eventDetail.title')}
            onBack={() => navigateTo('calendars')}
          />
        }
      >
        {null}
      </ScreenLayout>
    );
  }

  const startDate = typeof event.startDate === 'string' ? new Date(event.startDate) : event.startDate;
  const endDate = typeof event.endDate === 'string' ? new Date(event.endDate) : event.endDate;
  const isSameDay = startDate.toDateString() === endDate.toDateString();

  return (
    <ScreenLayout
      {...screenLayoutProps}
      formHeader={
        <FormHeader
          title={t('calendar.eventDetail.title')}
          onBack={() => {
            if (event.calendarId) {
              navigateTo('calendar-events', event.calendarId);
            } else {
              navigateTo('calendars');
            }
          }}
          actions={[
            { icon: 'create-outline', onPress: handleEdit, label: 'Edit', accessibilityLabel: 'Edit event' },
            { icon: 'trash-outline', onPress: handleDelete, label: 'Delete', accessibilityLabel: 'Delete event' },
          ]}
        />
      }
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Title */}
        <View style={styles.section}>
          <Text style={styles.title}>{event.title}</Text>
          {event.eventStatus && event.eventStatus !== 'scheduled' && (
            <View
              style={[
                styles.statusBadge,
                {
                  backgroundColor:
                    event.eventStatus === 'completed'
                      ? colors.success + '20'
                      : event.eventStatus === 'in_progress'
                      ? colors.primary + '20'
                      : colors.error + '20',
                },
              ]}
            >
              <Text
                style={[
                  styles.statusText,
                  {
                    color:
                      event.eventStatus === 'completed'
                        ? colors.success
                        : event.eventStatus === 'in_progress'
                        ? colors.primary
                        : colors.error,
                  },
                ]}
              >
                {event.eventStatus.replace('_', ' ').toUpperCase()}
              </Text>
            </View>
          )}
        </View>

        {/* Date & Time */}
        <View style={styles.section}>
          <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="calendar-outline" size={20} color={colors.primary} />
            <Text style={styles.sectionTitle}>{t('calendar.eventDetail.dateTimeSection')}</Text>
          </View>
          <Text style={styles.detailText}>
            {formatDate(startDate)}
          </Text>
          {event.isAllDay ? (
            <Text style={styles.detailSubtext}>{t('calendar.eventDetail.allDay')}</Text>
          ) : (
            <>
              <Text style={styles.detailText}>
                {formatTime(startDate)} - {formatTime(endDate)}
              </Text>
              {!isSameDay && (
                <Text style={styles.detailSubtext}>
                  {t('calendar.eventDetail.until', { date: formatDate(endDate) })}
                </Text>
              )}
            </>
          )}
          {event.timezone && (
            <Text style={styles.detailSubtext}>{t('calendar.eventDetail.timezone', { timezone: event.timezone })}</Text>
          )}
        </View>

        {/* Description */}
        {event.description && (
          <View style={styles.section}>
            <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="document-text-outline" size={20} color={colors.primary} />
              <Text style={styles.sectionTitle}>{t('calendar.eventDetail.descriptionSection')}</Text>
            </View>
            <Text style={styles.detailText}>{event.description}</Text>
          </View>
        )}

        {/* Location */}
        {event.meetingType === 'offline' && event.location?.name && (
          <View style={styles.section}>
            <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="location-outline" size={20} color={colors.primary} />
              <Text style={styles.sectionTitle}>{t('calendar.eventDetail.locationSection')}</Text>
            </View>
            <Text style={styles.detailText}>{event.location.name}</Text>
            {event.location.address && (
              <Text style={styles.detailSubtext}>{event.location.address}</Text>
            )}
          </View>
        )}

        {/* Online Meeting */}
        {(event.meetingType === 'online' || event.meetingType === 'hybrid') &&
          event.onlineMeeting?.joinUrl && (
            <View style={styles.section}>
              <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="videocam-outline" size={20} color={colors.primary} />
                <Text style={styles.sectionTitle}>{t('calendar.eventDetail.onlineMeetingSection')}</Text>
              </View>
              <Text style={styles.detailText}>
                {event.onlineMeeting.provider
                  ? event.onlineMeeting.provider.charAt(0).toUpperCase() +
                    event.onlineMeeting.provider.slice(1).replace('_', ' ')
                  : t('calendar.eventDetail.customProvider')}
              </Text>
              <TouchableOpacity
                style={[styles.joinButton, { flexDirection: rtl.rowDirection }]}
                onPress={handleJoinMeeting}
              >
                <Ionicons name="videocam" size={20} color={colors.white} />
                <Text style={styles.joinButtonText}>{t('calendar.eventDetail.joinMeeting')}</Text>
              </TouchableOpacity>
              {event.onlineMeeting.password && (
                <Text style={styles.detailSubtext}>
                  {t('calendar.eventDetail.password', { password: event.onlineMeeting.password })}
                </Text>
              )}
            </View>
          )}

        {/* Attachments */}
        {(() => {
          // Check for attachments in various possible locations/structures
          const attachments = event.attachments || 
                            event.attachment || 
                            (Array.isArray(event.attachment) ? event.attachment : []);
          
          if (!attachments || !Array.isArray(attachments) || attachments.length === 0) {
            return null;
          }
          
          return (
            <View style={styles.section}>
              <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="attach-outline" size={20} color={colors.primary} />
                <Text style={styles.sectionTitle}>
                  {t('calendar.eventDetail.attachmentsSection', { count: attachments.length })}
                </Text>
              </View>
              {attachments.map((attachment: any, index: number) => {
              // Support both entity structure (url, name, type) and alternative structures
              const fileUrl = attachment.url || attachment.fileUrl || attachment.uri;
              const fileName = attachment.name || attachment.fileName;
              const mimeType = attachment.type || attachment.mimeType;
              const fileId = attachment._id || attachment.fileId || index;
              
              return (
                <TouchableOpacity
                  key={fileId}
                  style={[styles.attachmentItem, { flexDirection: rtl.rowDirection }]}
                  onPress={async () => {
                    if (fileUrl) {
                      let urlToOpen = fileUrl;
                      
                      // If it's a Firebase Storage URL, use the proxy endpoint to avoid permission issues
                      if (fileUrl.includes('firebasestorage.googleapis.com')) {
                        try {
                          const { getCurrentApiConfig } = await import('../config/api.config');
                          const apiConfig = getCurrentApiConfig();
                          urlToOpen = `${apiConfig.baseURL}/api/storage/media/proxy?url=${encodeURIComponent(fileUrl)}`;
                        } catch (error) {
                          console.error('Failed to get API config for proxy URL:', error);
                          // Fallback to original URL
                        }
                      }
                      
                      if (Platform.OS === 'web') {
                        window.open(urlToOpen, '_blank');
                      } else {
                        Linking.openURL(urlToOpen);
                      }
                    }
                  }}
                  activeOpacity={0.7}
                >
                  <Ionicons 
                    name={mimeType?.startsWith('image/') ? 'image-outline' : 
                          mimeType?.startsWith('video/') ? 'videocam-outline' :
                          mimeType?.includes('pdf') ? 'document-text-outline' :
                          'document-outline'} 
                    size={24} 
                    color={colors.primary} 
                  />
                  <View style={styles.attachmentInfo}>
                    <Text style={styles.attachmentName} numberOfLines={1}>
                      {fileName || t('calendar.eventDetail.attachmentFallback', { number: index + 1 })}
                    </Text>
                    {mimeType && (
                      <Text style={styles.attachmentType}>
                        {mimeType}
                      </Text>
                    )}
                  </View>
                  {fileUrl && (
                    <Ionicons name="open-outline" size={20} color={colors.text.secondary} />
                  )}
                </TouchableOpacity>
              );
            })}
            </View>
          );
        })()}

        {/* My RSVP — only shown when the current user is themselves an attendee on this event */}
        {(() => {
          const myAttendee = event.attendees?.find((a: any) => a.type === 'user' && a.userId === user?.id);
          if (!myAttendee) return null;
          return (
            <View style={styles.section}>
              <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="checkmark-done-outline" size={20} color={colors.primary} />
                <Text style={styles.sectionTitle}>{t('calendar.eventDetail.myRsvpSection')}</Text>
              </View>
              <View style={[styles.rsvpButtons, { flexDirection: rtl.rowDirection }]}>
                <TouchableOpacity
                  style={[styles.rsvpButton, myAttendee.status === 'accepted' && styles.rsvpButtonActive]}
                  onPress={() => handleRespond('accepted')}
                  disabled={!!respondingStatus}
                >
                  <Text style={[styles.rsvpButtonText, myAttendee.status === 'accepted' && styles.rsvpButtonTextActive]}>
                    {t('calendar.eventDetail.rsvpAccept')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.rsvpButton, myAttendee.status === 'tentative' && styles.rsvpButtonActive]}
                  onPress={() => handleRespond('tentative')}
                  disabled={!!respondingStatus}
                >
                  <Text style={[styles.rsvpButtonText, myAttendee.status === 'tentative' && styles.rsvpButtonTextActive]}>
                    {t('calendar.eventDetail.rsvpTentative')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.rsvpButton, myAttendee.status === 'declined' && styles.rsvpButtonActive]}
                  onPress={() => handleRespond('declined')}
                  disabled={!!respondingStatus}
                >
                  <Text style={[styles.rsvpButtonText, myAttendee.status === 'declined' && styles.rsvpButtonTextActive]}>
                    {t('calendar.eventDetail.rsvpDecline')}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          );
        })()}

        {/* Attendees */}
        {event.attendees && event.attendees.length > 0 && (
          <View style={styles.section}>
            <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="people-outline" size={20} color={colors.primary} />
              <Text style={styles.sectionTitle}>
                {t('calendar.eventDetail.attendeesSection', { count: event.attendees.length })}
              </Text>
            </View>
            {event.attendees.map((attendee: any, index: number) => (
              <View key={index} style={[styles.attendeeItem, { flexDirection: rtl.rowDirection }]}>
                <Text style={styles.detailText}>
                  {attendee.external?.name || attendee.userId || attendee.departmentId}
                </Text>
                <AttendeeStatusBadge status={attendee.status} />
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      <ConfirmationModal
        visible={showDeleteModal}
        title={t('calendar.eventDetail.deleteTitle')}
        message={t('calendar.eventDetail.deleteMessage')}
        confirmLabel={t('calendar.eventDetail.deleteConfirm')}
        cancelLabel={t('calendar.eventDetail.deleteCancel')}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
        loading={deleting}
        variant="danger"
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: spacing.md,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  centerText: {
    ...typography.body,
    color: colors.textLight,
    marginTop: spacing.md,
  },
  section: {
    marginBottom: spacing.lg,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    ...typography.h3,
    color: colors.text,
    fontWeight: '600',
  },
  title: {
    ...typography.h1,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 8,
    marginTop: spacing.xs,
  },
  statusText: {
    ...typography.caption,
    fontSize: 11,
    fontWeight: '600',
  },
  detailText: {
    ...typography.body,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  detailSubtext: {
    ...typography.caption,
    color: colors.textLight,
    marginTop: spacing.xs,
  },
  joinButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
    padding: spacing.md,
    borderRadius: 8,
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  joinButtonText: {
    ...typography.body,
    color: colors.white,
    fontWeight: '600',
  },
  attendeeItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  rsvpButtons: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  rsvpButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
  },
  rsvpButtonActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
  rsvpButtonText: {
    ...textStyles.body,
    fontWeight: '600',
    color: colors.textDark,
    fontSize: typography.fontSize.sm,
  },
  rsvpButtonTextActive: {
    color: colors.white,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    marginBottom: spacing.sm,
    gap: spacing.md,
  },
  attachmentInfo: {
    flex: 1,
  },
  attachmentName: {
    ...typography.body,
    color: colors.text,
    marginBottom: spacing.xs / 2,
  },
  attachmentType: {
    ...typography.caption,
    color: colors.textLight,
  },
});

